/**
 * The rail's Team list: everyone Dialpad has a status for, by department (Support first), on-duty
 * people first in each, each with a status dot and word. Off-duty people read quieter, with their
 * reason, and can be hidden ("Hide off duty", remembered per browser); your own row always shows,
 * with "Hand off" (the shift note panel).
 */
import { type TeamMember } from '@lsc/contract';
import { byDepartment, statusDot, statusWords } from '../../lib/team';

interface Props {
  team: TeamMember[];
  viewerEmail: string;
  onHandOff: () => void;
  hideOffDuty: boolean;
  onHideOffDuty: (hide: boolean) => void;
}

const onDutyFirst = (people: TeamMember[]) => [...people].sort((a, b) => Number(b.onDuty) - Number(a.onDuty) || a.name.localeCompare(b.name));

export function TeamList({ team, viewerEmail, onHandOff, hideOffDuty, onHideOffDuty }: Props) {
  if (team.length === 0) return <p className="px-2.5 text-hint text-ink-3">No one has a Dialpad status yet.</p>;
  const me = viewerEmail.toLowerCase();
  const visible = (m: TeamMember) => !hideOffDuty || m.onDuty || m.email === me;
  const hidden = team.filter((m) => !visible(m)).length;
  const groups = byDepartment(team.filter(visible)).map((g) => ({ ...g, people: onDutyFirst(g.people) }));
  const offDuty = team.filter((m) => !m.onDuty).length;

  return (
    <div className="flex flex-col gap-2">
      {(offDuty > 0 || hideOffDuty) && (
        <button
          type="button"
          onClick={() => {
            onHideOffDuty(!hideOffDuty);
          }}
          className="self-start rounded-control px-2.5 py-0.5 text-hint text-info hover:bg-surface-2"
        >
          {hideOffDuty ? `Show off duty (${String(hidden)})` : 'Hide off duty'}
        </button>
      )}
      {groups.length === 0 && <p className="px-2.5 text-hint text-ink-3">No one is on duty right now.</p>}
      {groups.map((g) => (
        <div key={g.key} role="group" aria-label={g.label}>
          <p className="px-2.5 text-hint text-ink-3">{g.label}</p>
          <ul className="mt-0.5 flex flex-col">
            {g.people.map((m) => (
              <li key={m.email} className={`flex items-start gap-2 rounded-control px-2.5 py-1 text-hint ${m.onDuty ? 'text-ink-2' : 'text-ink-3'}`}>
                <span className={`mt-1.5 size-2 shrink-0 rounded-full ${statusDot(m)}`} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body">{m.name}</span>
                  <span className="block truncate" title={statusWords(m)}>
                    {statusWords(m)}
                  </span>
                  {m.email === me && (
                    <button type="button" onClick={onHandOff} className="-ml-1 mt-0.5 rounded-control px-1 py-0.5 text-hint text-info hover:bg-surface-2">
                      Hand off
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
